import { Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { Card, EmptyState, Loading, ModuleHero, Notice, PersonCard, StatCard, StatusPill } from '../../components/ui';
import { GradeDial } from '../../components/RatingReportView';
import { IconLock } from '../../components/NavIcons';
import { COMPANY_STATUS, TRACK_TONE, formatMoney, timeAgo } from '../../lib/format';
import type { Company, CompanyDocument, Message, Payment, RatingReport, Requirement } from '../../types/workspace';
import CompanyLogo from '../../components/CompanyLogo';

type Overview = {
  company: Company | null;
  requirements?: Requirement[];
  documents?: CompanyDocument[];
  missing?: Requirement[];
  latestReport?: RatingReport | null;
  ratingLocked?: boolean;
  ratingFee?: Payment | null;
  remediationCounts?: Record<string, number>;
  messages?: Message[];
  partnerShares?: number;
  brief?: { started: boolean; customerConfirmedAt: string | null; analystApprovedAt: string | null } | null;
  pendingPayments?: number;
  fundings?: { amount: number; currency: string }[];
};

type Action = { title: string; body: string; to: string; cta: string };

function nextAction(o: Overview): Action {
  const c = o.company!;
  const missing = o.missing?.length || 0;
  const brief = o.brief;
  if (!c.classification && ['INVITED', 'INTAKE'].includes(c.status)) {
    return { title: 'Choose your company type', body: 'It decides your rating track and the documents we ask for.', to: '/company', cta: 'Complete profile' };
  }
  if (o.ratingLocked) {
    return { title: 'Your rating is ready', body: 'Pay the rating fee on step 3 to unlock your full credit rating.', to: '/rating', cta: 'Pay & view rating' };
  }
  switch (c.status) {
    case 'INVITED':
    case 'INTAKE':
      return missing
        ? { title: `Upload ${missing} required document${missing > 1 ? 's' : ''}`, body: 'Every document is checked by AI as soon as you upload it.', to: '/documents', cta: 'Go to documents' }
        : { title: 'Submit your document pack', body: 'All required documents are in. Submit to get your Micus analyst and credit rating.', to: '/documents', cta: 'Review & submit' };
    case 'SUBMITTED':
      return brief?.started
        ? { title: 'Your rating is being prepared', body: 'Your Micus analyst is reviewing documents. Keep refining your Ready summary meanwhile.', to: '/ready', cta: 'Open Ready summary' }
        : { title: 'Your rating is being prepared', body: 'While your analyst works, start the company summary partners will read.', to: '/ready', cta: 'Start Ready summary' };
    case 'REANALYSIS':
      return { title: 'Review in progress', body: 'Your analyst is refreshing the rating with your updated documents.', to: '/rating', cta: 'View rating' };
    case 'REMEDIATING':
    case 'RATED':
      if (!brief?.customerConfirmedAt) {
        return { title: 'Confirm your Ready summary', body: 'Agree the company summary with your analyst — or update documents and submit for another review.', to: '/ready', cta: 'Open Ready' };
      }
      return { title: 'Approved for partner sharing', body: 'Your Micus analyst will send the rating and company summary to funding partners.', to: '/ready', cta: 'View Ready' };
    case 'READY':
      return { title: 'Partner outreach is next', body: 'You approved sharing. Your Micus analyst will contact funding partners and message you if anything else is needed.', to: '/partners', cta: 'Open Partners' };
    case 'OUTREACH':
      return { title: 'With funding partners', body: 'Your analyst is running partner conversations. You will see Funded when amounts and dates are recorded.', to: '/funded', cta: 'Open Funded' };
    case 'FUNDED':
      return o.pendingPayments
        ? { title: 'Funded — Micus fee due', body: 'Congratulations! Settle the Micus success fee for your disbursement.', to: '/funded', cta: 'See funding' }
        : { title: 'Funded', body: 'Your funding is recorded. See the numbers any time.', to: '/funded', cta: 'See funding' };
    default:
      return { title: 'Open your journey', body: 'Pick up where you left off.', to: '/company', cta: 'Continue' };
  }
}

export default function CustomerOverview() {
  const { data, loading, error } = useApi<Overview>('/customer/overview');

  if (loading) return <Loading />;
  if (error) return <Notice tone="rose">{error}</Notice>;

  if (!data?.company) {
    return (
      <EmptyState title="No company selected" body="Pick a company from My companies, or register a new one." cta={{ label: 'My companies', to: '/' }} />
    );
  }

  const c = data.company;
  const status = COMPANY_STATUS[c.status] || { label: c.status, tone: 'slate' as const, hint: '' };
  const action = nextAction(data);
  const docs = data.documents || [];
  const required = (data.requirements || []).filter((r) => r.required).length;
  const funded = (data.fundings || []).reduce((m, f) => m.set(f.currency, (m.get(f.currency) || 0) + f.amount), new Map<string, number>());
  const fundedLabel = Array.from(funded).map(([cur, amt]) => formatMoney(amt, cur)).join(' + ') || '—';

  return (
    <div>
      <ModuleHero
        eyebrow={c.workspaceLabel || 'Company overview'}
        title={c.logicalName || c.name}
        description={status.hint}
        actions={
          <div className="flex items-center gap-2">
            <CompanyLogo companyId={c.id} name={c.logicalName || c.name} hasLogo={c.hasLogo} className="h-12 w-12 rounded-xl" />
            <div className="flex flex-wrap gap-2">
              {c.classification && <StatusPill label={`${c.classification.type.label} · ${c.classification.track.label}`} tone={TRACK_TONE[c.classification.track.code]} />}
              <StatusPill label={status.label} tone={status.tone} />
            </div>
          </div>
        }
      />

      <div
        className="mz-fade-up mz-d1 mt-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl p-4 text-white"
        style={{ background: 'linear-gradient(135deg, #7a0038 0%, #c4005e 55%, #ff4fa2 100%)', boxShadow: '0 18px 40px rgba(214,0,110,0.25)' }}
      >
        <div className="max-w-xl">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/70">Next step</p>
          <p className="mt-1 text-lg font-black tracking-tight">{action.title}</p>
          <p className="mt-1 text-sm text-white/80">{action.body}</p>
        </div>
        <Link to={action.to} className="rounded-xl bg-white px-4 py-2 text-sm font-bold text-[#d6006e] shadow-lg">
          {action.cta} →
        </Link>
      </div>

      <div className="mz-fade-up mz-d2 mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Documents" value={`${docs.length}/${required}`} hint={`${data.missing?.length || 0} required missing`} to="/documents" />
        <StatCard
          label="Rated"
          value={data.ratingLocked ? 'Locked' : data.latestReport ? data.latestReport.grade : '—'}
          hint={data.ratingLocked ? 'pay rating fee' : 'credit rating'}
          tone={data.ratingLocked ? 'amber' : 'pink'}
          to="/rating"
        />
        <StatCard label="Partners" value={data.partnerShares || 0} hint="via Micus analyst" tone="blue" to="/ready" />
        <StatCard label="Funded" value={fundedLabel} hint={data.pendingPayments ? `${data.pendingPayments} payment(s) due` : 'disbursed'} tone="green" to="/funded" />
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
        <Card title="Latest rating" action={<Link to="/rating" className="text-xs font-bold text-[#d6006e]">Open →</Link>}>
          {data.latestReport ? (
            <div className="flex items-center gap-4">
              <GradeDial grade={data.latestReport.grade} score={data.latestReport.score} size={88} />
              <p className="text-sm leading-relaxed text-[#4a1a30]">{data.latestReport.summary}</p>
            </div>
          ) : data.ratingLocked ? (
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#fdf2f8] text-[#d6006e]">
                <IconLock className="h-5 w-5" />
              </span>
              <p className="text-sm text-[#6b3a52]">
                Your rating is published.{' '}
                <Link to="/rating" className="font-bold text-[#d6006e] underline">
                  Pay the rating fee
                </Link>{' '}
                to view it.
              </p>
            </div>
          ) : (
            <p className="text-sm text-[#c084a0]">Your credit rating appears here once your Micus analyst publishes it.</p>
          )}
        </Card>

        <div className="space-y-4">
          <PersonCard person={c.assignedAdmin} label="Your Micus analyst" empty="Assigned automatically when you submit documents" />
          <Card title="Recent activity">
            {(data.messages || []).length === 0 ? (
              <p className="text-sm text-[#c084a0]">No activity yet.</p>
            ) : (
              <ul className="space-y-2.5">
                {(data.messages || []).map((m) => (
                  <li key={m.id} className="flex gap-2.5 text-sm">
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${m.kind === 'SYSTEM' ? 'bg-[#f9c0d8]' : 'bg-[#d6006e]'}`} />
                    <div className="min-w-0">
                      <p className="line-clamp-2 text-[#4a1a30]">
                        {m.kind === 'MESSAGE' && m.author ? <strong>{m.author.name || m.author.email}: </strong> : null}
                        {m.body}
                      </p>
                      <p className="text-[11px] text-[#c084a0]">{timeAgo(m.createdAt)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
